import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../auth/AuthContext';
import {
  authApi,
  AdminUser,
  AuthApiError,
  createUser,
  listUsers,
  setUserPermissions,
  setUserDisabled,
} from '../auth/api';
import {
  APPROVAL_POLICY_STEPS,
  ApprovalPolicyStep,
  ApproverOption,
  ApprovalPolicies,
  approvalsApi,
} from '../architect/api';
import StagePolicyPreferences, { StagePolicyNotifications, PolicyPreferences, policyPreferences, samePolicyPreferences } from '../architect/StagePolicyPreferences';
import { approvalPolicyDraft } from '../architect/approvalPolicyDraft';
import './RootPanel.css';

// ═══════════════════════════════════════════════════════════
//  RootPanel — root-only account administration
//
//  Ported from the static Architect SPA's Users modal, restyled to
//  the dark house design. Three "roles" are frontend permission
//  bundles (the backend has no roles concept):
//    · developer        — main admin: catalog minus architect:use
//                         and admin:* (feedback:* stays in — intended)
//    · architect        — Architect Creator: ['architect:use']
//    · manager          — feedback only: ['feedback:submit','feedback:view']
//
//  No password reset / disable / delete endpoints exist, so the
//  password is shown ONCE at creation and copied by the root.
// ═══════════════════════════════════════════════════════════

const ARCHITECT_PERM = 'architect:use';
const FEEDBACK_PERMS = ['feedback:submit', 'feedback:view'];
const FEEDBACK_SET = new Set(FEEDBACK_PERMS);
// A programmer is an explicitly enabled Developers member, not an Architect approver.
const PROGRAMMER_PERMS = ['claude:view', 'claude:assign', 'analytics:view', 'gitlab:view', 'gitlab:manage', 'tasks:view', 'workers:chat'];
const PROGRAMMER_MEMBERSHIP = ['claude:view', 'claude:assign'];

// Architect sub-roles (DESIGN10 §P4): grantable ONLY to a holder of
// architect:use. Generator can send for estimate + generate/approve the
// proposal; Estimator edits + approves estimates. Root assigns them here.
const GENERATE_PERM = 'architect:generate';
const ESTIMATE_PERM = 'architect:estimate';
const PRICING_PERM = 'architect:pricing';
const GENERATE_HINT =
  'Генератор: работает над постером, отправляет на оценку, видит оценку после апрува, ' +
  'генерирует и апрувит предложение.';
const ESTIMATE_HINT =
  'Оценщик: видит архитектуру и оценки, редактирует их (редактор + AI-чат), апрувит оценку.';

type Role = 'programmer' | 'developer' | 'architect' | 'pricing' | 'manager' | 'root';

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: 'programmer', label: 'Программист — Developers, проекты и роботы' },
  { value: 'developer', label: 'Developer — основная админка' },
  { value: 'architect', label: 'Architect Creator — только Architect' },
  { value: 'pricing', label: 'Pricing — цены и апрув Pricing' },
  { value: 'manager',   label: 'Manager — только фидбэк' },
  { value: 'root',      label: 'Root — полный доступ (второй root)' },
];

const ROLE_NAME: Record<Role, string> = {
  programmer: 'Программист',
  developer: 'Developer',
  architect: 'Architect Creator',
  pricing: 'Pricing',
  manager: 'Manager',
  root: 'Root',
};

const USERNAME_RE = /^[a-zA-Z0-9_.-]+$/;

// Role badge: mirrors the create-role bundles. Order matters — Root
// wins, then the confined subsets (architect / feedback-only), and
// only a set holding a NON-confined permission reads as Developer:
// "Developer" implies main-admin access, which confined sets lack.
type BadgeKind = 'root' | 'architect' | 'pricing' | 'manager' | 'developer' | 'none';

function badgeFor(u: AdminUser): { kind: BadgeKind; label: string } {
  if (u.isRoot) return { kind: 'root', label: 'Root' };
  const perms = u.permissions || [];
  if (!perms.length) return { kind: 'none', label: 'Без прав' };
  if (PROGRAMMER_MEMBERSHIP.every(p => perms.includes(p))) return { kind: 'developer', label: 'Программист' };
  if (perms.every((p) => p === ARCHITECT_PERM || p === GENERATE_PERM || p === ESTIMATE_PERM || p === PRICING_PERM || FEEDBACK_SET.has(p))) {
    // Fully confined account: architect grant wins the label.
    if (perms.includes(ARCHITECT_PERM)) {
      if (perms.includes(PRICING_PERM) && !perms.includes(GENERATE_PERM) && !perms.includes(ESTIMATE_PERM)) {
        return { kind: 'pricing', label: 'Pricing' };
      }
      return { kind: 'architect', label: 'Architect Creator' };
    }
    return { kind: 'manager', label: 'Manager' };
  }
  return { kind: 'developer', label: 'Developer' };
}

// dd.mm.yyyy, hh:mm (ru-RU) — used for the Russian last-login lines.
function fmtDate(iso: string | null): string {
  if (!iso) return 'Ещё не входил';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Ещё не входил';
  return d.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// dd/mm/yyyy, hh:mm (en-GB) — used inside the English Telegram Mini App block
// (DESIGN15 §F2.4), matching the architect surface's date style. Only called
// with a non-null value; returns '' otherwise so callers never print a stray.
function fmtDateEn(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// Translate backend error codes to Russian copy. Unknown codes and
// validation errors fall through to the server message so we never
// hide useful detail.
function humanizeError(err: unknown): string {
  const code = err instanceof AuthApiError ? err.code : '';
  const message = err instanceof Error ? err.message : String(err);
  const map: Record<string, string> = {
    user_exists: 'Такой пользователь уже есть.',
    user_not_found: 'Пользователь не найден.',
    forbidden: 'Нет прав — нужен root.',
    unauthorized: 'Сессия закончилась — войдите заново.',
  };
  if (map[code]) return map[code];
  // invalid_username / invalid_email / invalid_password / invalid_permissions
  // carry a descriptive server message — surface it verbatim.
  return message || 'Не удалось выполнить запрос.';
}

/* ═══════════════════════════════════════════════════════════
   Telegram Mini App wiring (DESIGN14 §F2.3) — «сетап через админку».
   Root binds architect accounts to the mini app by issuing invite
   links; every eligible user card gets a status line + actions. The
   endpoints live under /api/tma (root-gated server-side); the global
   fetchInterceptor attaches the bearer token, so these plain fetches
   need no auth plumbing. Errors degrade gracefully — a backend without
   the TMA env (or an older build without the router) simply reads as
   «не настроен», never a broken panel.
   ═══════════════════════════════════════════════════════════ */

const TMA_BASE =
  (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '') +
  '/api/tma';

/** Only root accounts and holders of architect:use can enter the mini app
    (mirror of the backend v1 access rule), so only they get an invite block. */
function tmaEligible(u: AdminUser): boolean {
  return !!u.isRoot || (u.permissions || []).includes(ARCHITECT_PERM);
}

/* GET /api/tma/config (DESIGN26 §C2). Everything past `configured` is
   optional on purpose: a backend from before this round answers with the
   three original fields only, and the card has to degrade into «connect me»
   rather than render a row of blanks. */
interface TmaConfig {
  configured: boolean;
  needs_reconfigure?: boolean;
  source?: 'db' | 'env' | null;
  bot_username?: string | null;
  app_name?: string | null;
  bot_id?: number | string | null;
  /** First 6 hex characters of sha256(bot token). NOT a piece of the token:
      it names WHICH token is installed (hash your own copy and compare) and
      reveals nothing about it. */
  token_fingerprint?: string | null;
  /** A build of this endpoint from before the field was renamed answers with
      the last 4 characters of the token instead. Still read so the card fills
      in against one, but rendered as a mask and never as a fingerprint — the
      two make opposite claims and must not share copy. */
  token_hint?: string | null;
  verified_at?: string | null;
  updated_at?: string | null;
  updated_by_name?: string | null;
  encryption?: {
    at_rest?: string | null;
    key_source?: 'dedicated' | 'derived' | null;
    key_id?: string | null;
  } | null;
  invite_ttl_hours?: number | null;
  /** Exactly what an invite link will look like, with «<invite>» standing in
      for the token — so a short name that opens the bot's profile instead of
      the app is visible before any link is handed out. */
  link_preview?: string | null;
  link_kind?: 'direct' | 'main' | null;
}

/** PUT /api/tma/config — the server still saves when Telegram is unreachable
    and says so; an outright rejection is a 400 that stores nothing. */
interface TmaConfigSaveResponse {
  verified?: boolean;
  /** Why getMe did not answer usefully: 'unreachable' — nothing came back;
      'rejected' — something came back but held no bot. Read as a plain string
      so a future third reason degrades to the neutral copy. */
  verify_error?: 'unreachable' | 'rejected' | string | null;
  bot_username?: string | null;
}
interface TmaLinkRow {
  user_id: string;
  username?: string | null;
  display_name?: string | null;
  telegram_username?: string | null;
  linked_at?: string | null;
}
interface TmaInviteRow {
  user_id: string;
  expires_at?: string | null;
}
interface TmaLinksResponse {
  links?: TmaLinkRow[];
  // The server calls the pending-invite array `pending`; older/alternate
  // builds called it `invites`. Read both so the «Invited» state survives a
  // reload either way (it is derived, never authored here).
  pending?: TmaInviteRow[];
  invites?: TmaInviteRow[];
}
interface TmaInviteResponse {
  token: string;
  link: string;
  expires_at?: string | null;
}

/** Per-user mini-app status, folded from /links (linked rows + pending
    invites) so each card can render its state in one glance. */
type TmaUserState =
  | { kind: 'linked'; telegramUsername: string | null; linkedAt: string | null }
  | { kind: 'invited'; expiresAt: string | null }
  | { kind: 'none' };

/** A 400 from the TMA layer points at the field it choked on; AuthApiError
    carries only the code, so widen it here rather than reshape the auth
    client every other panel shares. */
class TmaApiError extends AuthApiError {
  field?: string;
  constructor(code: string, message?: string, field?: string) {
    super(code, message);
    this.field = field;
  }
}

async function tmaGet<T>(path: string): Promise<T> {
  const res = await fetch(TMA_BASE + path);
  if (!res.ok) throw new AuthApiError('request_failed', 'HTTP ' + res.status);
  return res.json();
}
async function tmaSend<T>(path: string, method: string, body?: unknown): Promise<T> {
  const res = await fetch(TMA_BASE + path, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const data = await res
      .json()
      .catch(() => ({} as { error?: string; message?: string; field?: string }));
    throw new TmaApiError(data.error || 'request_failed', data.message, data.field);
  }
  return res.json();
}

// Backend TMA error codes → English copy (white-label, house tone). The mini
// app this block configures is fully English (DESIGN15 §F2.4), so its errors
// are too, even though the surrounding RootPanel stays Russian.
function humanizeTmaError(err: unknown): string {
  const code = err instanceof AuthApiError ? err.code : '';
  const message = err instanceof Error ? err.message : String(err);
  const map: Record<string, string> = {
    // …from /api/tma itself (routes.ts).
    user_not_eligible:
      'This user cannot be added — Architect access or root is required.',
    user_not_found: 'That account no longer exists — reload the page.',
    tma_not_configured: 'The Mini App is not configured on the server yet.',
    cannot_disable_self: 'Себя отключить нельзя.',
    last_root: 'Нельзя отключить последнего активного рута.',
    token_rejected:
      'Telegram rejected this bot token — copy it again from @BotFather.',
    bad_request: 'The form could not be saved — check the fields.',
    // …from the root gate every one of these routes sits behind.
    unauthorized: 'The session has expired — sign in again.',
    forbidden: 'Not allowed — root is required.',
    // …from the server's own edges: the 500 handler, and a reply that was
    // not JSON at all (a proxy page, a dead backend).
    internal_error:
      'The server could not complete that — try again, and check the server log if it keeps failing.',
    request_failed:
      'The server could not be reached — check that it is running.',
    secret_undecryptable:
      'The stored token can no longer be decrypted — reconnect the Mini App.',
  };
  // bad_request carries a field-level message from the server; that message
  // is the whole point of the code, so it outranks the generic line above.
  if (code === 'bad_request' && message && message !== code) return message;
  if (map[code]) return map[code];
  // AuthApiError falls back to the CODE as its message, so anything unmapped
  // would otherwise print a machine word at the operator. Only a genuine
  // server sentence gets through here.
  if (message && message !== code) return message;
  return 'The request could not be completed.';
}

/* ═══════════════════════════════════════════════════════════
   Mini App connection (DESIGN26 §D) — «а можно сетапить и приглашать,
   понятное дело чтобы всё шифровалось». Root connects the bot from the
   product instead of reading a card that says «set three env vars and
   restart the server».

   The token is a bearer credential for the whole bot, so it lives in
   this component's state only as long as the input holds it: sent once,
   cleared the moment the server accepts it, never rendered back. The
   status card knows nothing but the few characters of hint the server
   keeps for exactly this purpose.

   The env vars remain a supported source — an install wired that way
   reads as «from the server environment» and has no row to edit or
   delete, only to be taken over by a stored (encrypted) token.
   ═══════════════════════════════════════════════════════════ */

// Mirrors of the server-side shapes (DESIGN26 §C2). Checking them here turns
// a round-trip into an instant answer; the server still has the last word.
const BOT_TOKEN_RE = /^\d{6,}:[A-Za-z0-9_-]{30,}$/;
const APP_NAME_RE = /^[A-Za-z0-9_]{3,64}$/;
const BOT_USERNAME_RE = /^[A-Za-z0-9_]{4,64}$/;

type TmaField = 'bot_token' | 'app_name' | 'bot_username';

/** Which input a server error belongs under. The 400 body may name the field
    outright; when it doesn't, read it out of the message — an error above the
    form is better than an error under the wrong input, so give up on `null`
    rather than guess. */
function fieldFromTmaError(err: unknown, message: string): TmaField | null {
  if (err instanceof TmaApiError) {
    if (err.field === 'bot_token' || err.field === 'app_name' || err.field === 'bot_username') {
      return err.field;
    }
    if (err.code === 'token_rejected') return 'bot_token';
  }
  const m = message.toLowerCase();
  if (m.includes('bot_token') || m.includes('token')) return 'bot_token';
  if (m.includes('app_name') || m.includes('app short name')) return 'app_name';
  if (m.includes('username')) return 'bot_username';
  return null;
}

const TmaSetupCard: React.FC<{
  config: TmaConfig;
  /** Re-read /config + /links after a write — the server is the truth. */
  onChanged: () => Promise<void> | void;
}> = ({ config, onChanged }) => {
  const configured = !!config.configured;
  const needsReconfigure = !!config.needs_reconfigure;
  const fromEnv = config.source === 'env';

  const [editing, setEditing] = useState(false);
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [appName, setAppName] = useState('');
  const [botUsername, setBotUsername] = useState('');
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<TmaField, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'warn'; text: string } | null>(null);

  // Nothing configured, or nothing usable — then the form IS the card.
  const formOpen = editing || !configured || needsReconfigure;

  // Keep the two non-secret fields seeded from what the server already knows,
  // so reconnecting after a key change is a one-field job. Never the token:
  // it is not ours to remember, and the server cannot give it back anyway.
  useEffect(() => {
    if (editing) return;
    setAppName(config.app_name || '');
    setBotUsername(config.bot_username || '');
  }, [editing, config.app_name, config.bot_username]);

  const openForm = useCallback(() => {
    setEditing(true);
    setToken('');
    setShowToken(false);
    setFieldErrors({});
    setFormError(null);
    setNotice(null);
  }, []);

  const closeForm = useCallback(() => {
    setEditing(false);
    setToken('');
    setShowToken(false);
    setFieldErrors({});
    setFormError(null);
  }, []);

  const submitConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;

    const tok = token.trim();
    const app = appName.trim();
    const user = botUsername.trim().replace(/^@/, '');
    const errors: Partial<Record<TmaField, string>> = {};
    if (!BOT_TOKEN_RE.test(tok)) {
      errors.bot_token =
        'That does not look like a bot token — @BotFather gives you digits, a colon, then a long secret.';
    }
    // The short name is optional (empty = the bot's main Mini App); only a
    // non-empty value has to look like a /newapp short name.
    if (app && !APP_NAME_RE.test(app)) {
      errors.app_name = '3–64 characters: letters, digits and underscore.';
    }
    if (user && !BOT_USERNAME_RE.test(user)) {
      errors.bot_username = '4–64 characters: letters, digits and underscore.';
    }
    setFieldErrors(errors);
    setFormError(null);
    setNotice(null);
    if (Object.keys(errors).length) return;

    setSaving(true);
    try {
      const r = await tmaSend<TmaConfigSaveResponse>('/config', 'PUT', {
        bot_token: tok,
        app_name: app,
        ...(user ? { bot_username: user } : {}),
      });
      // The credential leaves this component the instant the server holds it.
      setToken('');
      setShowToken(false);
      setEditing(false);
      setNotice(
        r.verified === false
          ? {
              kind: 'warn',
              // The two failures need different hands on different keyboards:
              // 'unreachable' is a network problem to take to the ops side,
              // 'rejected' means something answered in Telegram's place and
              // the reply had no bot in it — a proxy, a portal, a wrong host.
              text:
                (r.verify_error === 'rejected'
                  ? 'Saved, but Telegram answered without naming a bot for this token — ' +
                    'something between this server and api.telegram.org may be rewriting ' +
                    'the reply. Check the token, and check the egress proxy.'
                  : 'Saved, but Telegram could not be reached to verify the token — ' +
                    'the request timed out or never left this server.') +
                ' The Mini App works with the username you entered; the verified ' +
                'date fills in on the next change.',
            }
          : {
              kind: 'ok',
              text:
                'Connected' +
                (r.bot_username ? ' as @' + r.bot_username : '') +
                '. Any pending invites were voided — issue new links.',
            },
      );
      await onChanged();
    } catch (err) {
      const message = humanizeTmaError(err);
      const field = fieldFromTmaError(err, message);
      if (field) setFieldErrors({ [field]: message });
      else setFormError(message);
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    if (
      !window.confirm(
        'Disconnect the Mini App? The stored token is deleted and every pending ' +
          'invite is voided — linked users keep their accounts but cannot sign in.',
      )
    ) {
      return;
    }
    setDisconnecting(true);
    setFormError(null);
    setNotice(null);
    try {
      // Answers with the config that is left (the environment, or nothing);
      // the reload below adopts it rather than this copy.
      await tmaSend<TmaConfig>('/config', 'DELETE');
      setEditing(false);
      await onChanged();
    } catch (err) {
      setFormError(humanizeTmaError(err));
    } finally {
      setDisconnecting(false);
    }
  };

  // A fingerprint is an identifier, not a redaction, so it is shown whole and
  // labelled as what it is. The legacy hint IS key material, so it keeps the
  // mask — nothing here may suggest the operator is looking at their token.
  const tokenFingerprint = (config.token_fingerprint || '').trim().toLowerCase();
  // The server may or may not decorate the hint with an ellipsis; the mask
  // supplies its own, so strip whatever came with it.
  const tokenHint = (config.token_hint || '').replace(/^[…. ]+/, '');
  const keySource = config.encryption?.key_source || null;
  const keyId = config.encryption?.key_id || null;
  const ttlHours = config.invite_ttl_hours || 48;
  const showStatus = configured && !needsReconfigure;
  // No key material on the server at all: sealing the token would throw and
  // the save would come back as an opaque 500, so the form refuses instead.
  // Gated on `encryption` being present — a backend from before this round
  // sends no such block and must not be locked out by a field it never had.
  const noEncryptionKey = !!config.encryption && !keySource;

  return (
    <div className="root-tma-setup">
      <div className="root-tma-setup-head">
        <span className="root-tma-mark" aria-hidden="true">
          ✈
        </span>
        <span className="root-tma-setup-title">
          {showStatus ? 'Mini App' : 'Connect the Mini App'}
        </span>
        {showStatus && <span className="root-tma-status linked">Connected</span>}
        {needsReconfigure && (
          <span className="root-tma-status broken">Reconnect needed</span>
        )}
      </div>

      {needsReconfigure && (
        <div className="root-tma-alert">
          The stored token can no longer be decrypted (the encryption key
          changed). Reconnect the Mini App — until then sign-in and invites
          stay closed.
        </div>
      )}

      {showStatus && (
        <div className="root-tma-cfg">
          <div className="root-tma-cfg-id">
            {config.bot_username ? '@' + config.bot_username : 'Bot'}
            {config.app_name ? ' · ' + config.app_name : ''}
          </div>

          <div className="root-tma-cfg-rows">
            {tokenFingerprint ? (
              <div className="root-tma-cfg-row">
                <span className="root-tma-cfg-key">Fingerprint</span>
                <span
                  className="root-tma-cfg-val root-tma-fp"
                  title="First 6 hex characters of sha256(bot token) — an identifier for the stored token, not a part of it."
                >
                  sha256:{tokenFingerprint}
                </span>
              </div>
            ) : tokenHint ? (
              <div className="root-tma-cfg-row">
                <span className="root-tma-cfg-key">Token</span>
                <span className="root-tma-cfg-val root-tma-mask">•••• {tokenHint}</span>
              </div>
            ) : null}
            <div className="root-tma-cfg-row">
              <span className="root-tma-cfg-key">Verification</span>
              <span className={`root-tma-cfg-val${config.verified_at ? '' : ' muted'}`}>
                {config.verified_at
                  ? 'Verified ' + fmtDateEn(config.verified_at)
                  : 'Not verified'}
              </span>
            </div>
            {(config.updated_by_name || config.updated_at) && (
              <div className="root-tma-cfg-row">
                <span className="root-tma-cfg-key">Updated</span>
                <span className="root-tma-cfg-val muted">
                  {config.updated_by_name ? 'by ' + config.updated_by_name : ''}
                  {config.updated_by_name && config.updated_at ? ' · ' : ''}
                  {fmtDateEn(config.updated_at)}
                </span>
              </div>
            )}
          </div>

          {/* Without this line the fingerprint is a riddle: it exists so an
              operator holding a token can decide whether it is THE one. */}
          {tokenFingerprint && (
            <div className="root-tma-fp-note">
              A hash of the stored token, not a part of it — run
              <span className="root-tma-mono"> sha256</span> on your copy and
              compare the first six characters.
            </div>
          )}

          {/* The invite link, spelled out. A short name that does not exist
              does not error: Telegram opens the bot's PROFILE and the invite
              silently does nothing, so the only defence is showing the shape
              before links are handed out. */}
          {config.link_preview && (
            <div className="root-tma-linkprev">
              <span className="root-tma-cfg-key">Invite link</span>
              <span className="root-tma-mono root-tma-linkprev-url">{config.link_preview}</span>
              <span className="root-tma-linkprev-kind">
                {config.link_kind === 'main'
                  ? 'main Mini App — set it in BotFather → Bot Settings'
                  : 'direct link — needs a /newapp app with this short name'}
              </span>
            </div>
          )}

          {/* «Encrypted at rest» is a claim about the stored row — an install
              reading its token from the environment must not wear it. */}
          <div className="root-tma-badges">
            {!fromEnv && (
              <span className="root-tma-badge">
                Encrypted at rest
                {config.encryption?.at_rest ? ' · ' + config.encryption.at_rest : ''}
              </span>
            )}
            {!fromEnv && keyId && <span className="root-tma-badge">Key {keyId}</span>}
            <span className="root-tma-badge">Invites expire in {ttlHours}h</span>
            <span className="root-tma-badge">Single-use</span>
          </div>

          {fromEnv && (
            <div className="root-tma-envnote">
              Set from the server environment — there is no stored token here to
              change or delete. Connecting one below stores it encrypted in the
              database, and it takes over from the environment.
            </div>
          )}
        </div>
      )}

      {/* Same claim as the «Encrypted at rest» badge above, so it carries the
          same guard: an env-only install encrypts nothing, and a note about
          which key does the encrypting is noise there. */}
      {keySource === 'derived' && !fromEnv && (
        <div className="root-tma-note">
          Using a derived encryption key — set SECRETS_KEY for a dedicated one.
        </div>
      )}

      {/* No key material at all: the server reports it here rather than let
          root fill in a token that cannot be sealed and fails on save. */}
      {noEncryptionKey && (
        <div className="root-tma-alert">
          This server has no encryption key — set SECRETS_KEY (or JWT_SECRET)
          before connecting a bot. The token cannot be stored without one.
        </div>
      )}

      {notice && <div className={`root-tma-notice ${notice.kind}`}>{notice.text}</div>}

      {showStatus && !formOpen && (
        <div className="root-tma-cfg-actions">
          <button className="btn-secondary root-toggle" type="button" onClick={openForm}>
            {fromEnv ? 'Store an encrypted token' : 'Change token'}
          </button>
          {!fromEnv && (
            <button
              className="btn-secondary root-toggle root-tma-unlink"
              type="button"
              disabled={disconnecting}
              onClick={disconnect}
            >
              {disconnecting ? 'Disconnecting…' : 'Disconnect'}
            </button>
          )}
        </div>
      )}

      {formOpen && (
        <form className="root-tma-form" onSubmit={submitConfig}>
          {!configured && !needsReconfigure && (
            <p className="root-tma-setup-lead">
              The Telegram Mini App gives architects access to projects from
              their phone. Connect the bot here — the token is encrypted before
              it is stored, and it is never shown again.
            </p>
          )}

          <details className="root-tma-help">
            <summary>Where do I get these?</summary>
            <ol className="root-tma-setup-steps">
              <li>
                In <b>@BotFather</b>, create a bot — it answers with the token.
              </li>
              <li>
                Still in <b>@BotFather</b>, run
                <span className="root-tma-mono"> /newapp</span> for that bot and
                give the Mini App a short name.
              </li>
              <li>Paste the token and that short name below.</li>
            </ol>
            <p className="root-tma-help-env">
              The server variables
              <span className="root-tma-mono"> TELEGRAM_BOT_TOKEN</span>,
              <span className="root-tma-mono"> TELEGRAM_BOT_USERNAME</span>,
              <span className="root-tma-mono"> TELEGRAM_APP_NAME</span> still
              work as a fallback; whatever is connected here takes over.
            </p>
          </details>

          <div className="root-tma-form-grid">
            <div className="form-section root-tma-field-wide">
              <label className="form-label" htmlFor="tma-bot-token">
                Bot token
                <span className="form-hint">from @BotFather — stored encrypted, shown here once</span>
              </label>
              <div className="root-tma-secret">
                <input
                  id="tma-bot-token"
                  className="form-input"
                  type={showToken ? 'text' : 'password'}
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  value={token}
                  disabled={saving}
                  // Tokens are pasted far more often than typed, and a paste
                  // drags a newline along at least as often as it doesn't.
                  onChange={(e) => setToken(e.target.value.replace(/\s+/g, ''))}
                  placeholder="1234567890:AA…"
                />
                <button
                  className="root-tma-eye"
                  type="button"
                  aria-label={showToken ? 'Hide the token' : 'Show the token'}
                  aria-pressed={showToken}
                  onClick={() => setShowToken((v) => !v)}
                >
                  {showToken ? 'Hide' : 'Show'}
                </button>
              </div>
              {fieldErrors.bot_token && (
                <div className="root-tma-field-err">{fieldErrors.bot_token}</div>
              )}
            </div>

            <div className="form-section">
              <label className="form-label" htmlFor="tma-app-name">
                App short name
                <span className="form-hint">
                  optional — the /newapp one, NOT the bot username. Leave empty
                  to use the bot’s main Mini App.
                </span>
              </label>
              <input
                id="tma-app-name"
                className="form-input"
                type="text"
                autoComplete="off"
                spellCheck={false}
                value={appName}
                disabled={saving}
                onChange={(e) => setAppName(e.target.value.trim())}
                placeholder="architect — or leave empty"
                maxLength={64}
              />
              {fieldErrors.app_name && (
                <div className="root-tma-field-err">{fieldErrors.app_name}</div>
              )}
            </div>

            <div className="form-section">
              <label className="form-label" htmlFor="tma-bot-username">
                Bot username
                <span className="form-hint">optional — filled automatically when the token verifies</span>
              </label>
              <input
                id="tma-bot-username"
                className="form-input"
                type="text"
                autoComplete="off"
                spellCheck={false}
                value={botUsername}
                disabled={saving}
                onChange={(e) => setBotUsername(e.target.value.trim())}
                placeholder="lato_architect_bot"
                maxLength={64}
              />
              {fieldErrors.bot_username && (
                <div className="root-tma-field-err">{fieldErrors.bot_username}</div>
              )}
            </div>
          </div>

          {formError && <div className="form-result bad">{formError}</div>}

          <div className="root-tma-form-actions">
            <button
              className="btn-primary"
              type="submit"
              disabled={saving || noEncryptionKey || !token}
              title={
                noEncryptionKey
                  ? 'The server has no encryption key, so the token cannot be stored.'
                  : undefined
              }
            >
              {saving && <span className="root-tma-spin" aria-hidden="true" />}
              {saving ? 'Connecting…' : configured ? 'Save token' : 'Connect'}
            </button>
            {showStatus && (
              <button
                className="btn-secondary root-toggle"
                type="button"
                disabled={saving}
                onClick={closeForm}
              >
                Cancel
              </button>
            )}
            {/* The alert above the form explains the server state; this says
                why THIS button is dead, next to the dead button. */}
            {noEncryptionKey && (
              <span className="root-tma-blocked">
                Connecting is disabled until the server has an encryption key.
              </span>
            )}
          </div>

          <div className="root-tma-sec">
            Encrypted at rest (AES-256-GCM) · never displayed again · every
            change is recorded in the audit log
          </div>
        </form>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   Mini-app access (DESIGN24 §T3) — «застепаить чтобы можно было мини
   апп через рут и каждому привязать пользователя с его пермишинами».

   The invite doesn't carry any rights of its own: it binds a Telegram
   account to a CRM account, and the mini app then obeys exactly the
   permissions that account holds. So the card that hands out the invite
   is also the card that sets those permissions — the same bundles the
   create-user form offers, written through the existing
   POST /api/auth/users/:id/permissions (grant/revoke arrays).

   architect:use is the DOOR: the backend refuses the mini-app sign-in
   without it (tma_not_allowed) and 403s every architect route. The two
   sub-roles decide what is behind that door. Granting a sub-role to an
   account with no door therefore opens the door in the same request —
   the alternative is a permission that silently does nothing. Revoking
   is never widened the same way: taking «Architect access» away leaves
   the sub-roles on the account (exactly like the toggle above it), and
   they simply read as inert until access comes back.
   ═══════════════════════════════════════════════════════════ */

interface MiniGrant {
  key: 'use' | 'generate' | 'estimate' | 'pricing' | 'feedback';
  /** Pill label, and the name used in the «the invite will grant» summary. */
  label: string;
  /** The permission bundle this pill writes. */
  perms: string[];
  hint: string;
}

const MINI_GRANTS: readonly MiniGrant[] = [
  {
    key: 'use',
    label: 'Architect access',
    perms: [ARCHITECT_PERM],
    hint:
      'Opens the mini app: the project list, sources and poster generation. ' +
      'Without it the sign-in is refused.',
  },
  {
    key: 'generate',
    label: 'Generator',
    perms: [GENERATE_PERM],
    hint:
      'Approves the architecture, generates, edits and approves the proposal.',
  },
  {
    key: 'estimate',
    label: 'Estimator',
    perms: [ESTIMATE_PERM],
    hint:
      'Sends a project for estimate, edits the estimate document and approves it.',
  },
  {
    key: 'pricing',
    label: 'Pricing',
    perms: [PRICING_PERM],
    hint: 'Reviews pricing and approves it when assigned. Receives the pricing approval requests.',
  },
  {
    key: 'feedback',
    label: 'Feedback',
    perms: FEEDBACK_PERMS,
    hint: 'Leaves and reads architecture feedback and the knowledge base.',
  },
];

/** What a fresh mini-app sign-in would actually get this account:
     root    — everything, implicitly;
     blocked — no architect:use → the server turns the sign-in away;
     empty   — the door but no sub-role → nothing to do inside;
     ok      — at least one working role. */
type MiniState = 'root' | 'blocked' | 'empty' | 'ok';

function miniState(u: AdminUser): MiniState {
  if (u.isRoot) return 'root';
  const perms = u.permissions || [];
  if (!perms.includes(ARCHITECT_PERM)) return 'blocked';
  if (!perms.includes(GENERATE_PERM) && !perms.includes(ESTIMATE_PERM) && !perms.includes(PRICING_PERM)) return 'empty';
  return 'ok';
}

/* ═══════════════════════════════════════════════════════════
   Approvals (DESIGN23 §C4) — «на каждый шаг выбирать конкретных
   апрувров… 2 из 2, по дефолту любой».

   Root picks WHO may approve each pipeline step and HOW MANY approvals
   it takes. English copy, like the mini-app block above: this is a new
   surface and the whole architect product speaks English.

   THE RULE THIS PANEL MUST NEVER BREAK: a step with no policy behaves
   exactly as it does today — anyone holding the right, one approval.
   Saving a policy writes only the policy tables: it never removes a
   vote, never changes a document, and never demands a retro-approval of
   something already approved. The copy says so, out loud.
   ═══════════════════════════════════════════════════════════ */

const APPROVAL_STEP_LABEL: Record<ApprovalPolicyStep, string> = {
  architecture: 'Architecture',
  estimate: 'Estimate',
  pricing: 'Pricing',
  proposal: 'Proposal',
};

const APPROVAL_STEP_HINT: Record<ApprovalPolicyStep, string> = {
  architecture: 'Review the architecture',
  estimate: 'Review the development estimate',
  pricing: 'Review setup and monthly prices',
  proposal: 'Review the final proposal',
};

/** Who may be listed for a step (mirrors the backend eligibility rule, and
    the server re-checks it anyway — this is only the empty-list copy). */
const APPROVAL_STEP_ROLE: Record<ApprovalPolicyStep, string> = {
  architecture: 'Root or an account with the Generator sub-role.',
  estimate: 'Root or an account with the Estimator sub-role.',
  pricing: 'Grant the Pricing sub-role in Team.',
  proposal: 'Root or an account with the Generator sub-role.',
};

const MAX_REQUIRED = 5;

/** `ids` and `ordered` retain the named policy while Architecture's general
    review mode is enabled, so switching modes does not erase that policy. */
interface ApprovalDraft extends PolicyPreferences {
  required: number;
  ids: string[];
  ordered: boolean;
  generalReview?: boolean;
  generalReviewerId?: string | null;
  incompleteNamedApprovers?: boolean;
}

function approverLabel(u: ApproverOption): string {
  return u.display_name || u.username;
}

function ordinal(n: number): string {
  return n === 1 ? 'first' : n === 2 ? 'second' : n === 3 ? 'third' : `${n}th`;
}

/** The highest required count a draft may legally carry: capped by the chosen
    list when there is one (the server rejects more), otherwise the hard max. */
function requiredCap(ids: string[]): number {
  return ids.length ? Math.min(MAX_REQUIRED, ids.length) : MAX_REQUIRED;
}

function sameDraft(a: ApprovalDraft, b: ApprovalDraft): boolean {
  if (!samePolicyPreferences(a, b)) return false;
  if (Boolean(a.incompleteNamedApprovers) !== Boolean(b.incompleteNamedApprovers)) return false;
  if (Boolean(a.generalReview) !== Boolean(b.generalReview)) return false;
  if (a.generalReview && a.generalReviewerId !== b.generalReviewerId) return false;
  if (a.required !== b.required || a.ids.length !== b.ids.length) return false;
  if ((a.ordered && a.ids.length > 1) !== (b.ordered && b.ids.length > 1)) return false;
  // With an ordered list the ORDER is part of the policy.
  if (a.ordered && a.ids.length > 1) return a.ids.every((id, i) => b.ids[i] === id);
  const set = new Set(b.ids);
  return a.ids.every((id) => set.has(id));
}

const RootPanel: React.FC = () => {
  const { user } = useAuth();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Live catalog → developer bundle, so it never drifts from the server.
  const [devBundle, setDevBundle] = useState<string[] | null>(null);

  // Create-user form.
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>('programmer');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdHint, setCreatedHint] = useState<string | null>(null);

  // Per-row in-flight guard so double-clicks don't fire twice.
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  // ── Telegram Mini App (DESIGN14 §F2.3) ──
  const [tmaConfig, setTmaConfig] = useState<TmaConfig>({ configured: false });
  // Per-user mini-app state, keyed by user id (linked / invited / none).
  const [tmaStates, setTmaStates] = useState<Record<string, TmaUserState>>({});
  // The user whose invite link is in the open modal (null = closed).
  const [inviteFor, setInviteFor] = useState<AdminUser | null>(null);
  const [inviteData, setInviteData] = useState<TmaInviteResponse | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Which user's TMA action is in flight (invite create / unlink).
  const [tmaBusyId, setTmaBusyId] = useState<string | null>(null);

  // ── Approvals (DESIGN23 §C4) ──
  const [apprLoading, setApprLoading] = useState(true);
  // The SAVED policy per step (the baseline the draft is compared against).
  const [apprSaved, setApprSaved] = useState<Record<string, ApprovalDraft>>({});
  // The editable draft per step.
  const [apprDraft, setApprDraft] = useState<Record<string, ApprovalDraft>>({});
  // Who may be listed for each step (root-only, from GET /policies).
  const [apprEligible, setApprEligible] = useState<Record<string, ApproverOption[]>>({});
  const [apprAccessMetadata, setApprAccessMetadata] = useState<Pick<ApprovalPolicies, 'access_defaults' | 'root_user_ids'>>({});
  const [apprSavingStep, setApprSavingStep] = useState<string | null>(null);
  const [apprSavedStep, setApprSavedStep] = useState<string | null>(null);
  const [apprError, setApprError] = useState<string | null>(null);
  // The whole block is unavailable (older backend / migrations not run yet):
  // hide it rather than show a broken panel — the pipeline keeps working on
  // its default single-approval behaviour either way.
  const [apprUnavailable, setApprUnavailable] = useState(false);

  const reloadUsers = useCallback(async () => {
    try {
      const list = await listUsers();
      setUsers(list);
      setLoadError(null);
    } catch (err) {
      setLoadError(humanizeError(err));
    }
  }, []);

  // Fold /config + /links into the per-user state map. Any failure (no TMA
  // env, older backend) degrades to «не настроен» — never throws upward.
  const reloadTma = useCallback(async () => {
    try {
      const cfg = await tmaGet<TmaConfig>('/config');
      setTmaConfig(cfg && typeof cfg.configured === 'boolean' ? cfg : { configured: false });
    } catch {
      setTmaConfig({ configured: false });
      setTmaStates({});
      return;
    }
    try {
      const data = await tmaGet<TmaLinksResponse>('/links');
      const next: Record<string, TmaUserState> = {};
      for (const row of data.links || []) {
        next[row.user_id] = {
          kind: 'linked',
          telegramUsername: row.telegram_username ?? null,
          linkedAt: row.linked_at ?? null,
        };
      }
      for (const inv of data.pending || data.invites || []) {
        // A live invite only matters until the account links.
        if (next[inv.user_id]) continue;
        next[inv.user_id] = { kind: 'invited', expiresAt: inv.expires_at ?? null };
      }
      setTmaStates(next);
    } catch {
      setTmaStates({});
    }
  }, []);

  /* Load the four policies + the per-step eligible lists. A failure hides
     the block instead of shouting: with no policy every step already behaves
     as it always has, so an unavailable endpoint costs nothing. */
  const reloadApprovals = useCallback(async () => {
    try {
      const r = await approvalsApi.getPolicies();
      const saved: Record<string, ApprovalDraft> = {};
      for (const step of APPROVAL_POLICY_STEPS) {
        const row = (r.policies || []).find((p) => p.step === step);
        saved[step] = {
          ...approvalPolicyDraft(row),
          ...policyPreferences(row),
          ...(step === 'architecture' || row?.general_reviewer_id !== undefined ? {
            generalReview: Boolean(row?.general_reviewer_id),
            generalReviewerId: row?.general_reviewer_id ?? null,
          } : {}),
        };
      }
      const eligible: Record<string, ApproverOption[]> = {};
      for (const step of APPROVAL_POLICY_STEPS) {
        // Prefer the root-only directory; fall back to whoever is already
        // listed so a saved policy is never rendered as a set of blanks.
        const row = (r.policies || []).find((p) => p.step === step);
        eligible[step] = r.eligible?.[step] || [...(row?.users || []), ...(row?.general_reviewer ? [row.general_reviewer] : [])].filter((u, index, all) => all.findIndex((person) => person.id === u.id) === index);
      }
      setApprSaved(saved);
      setApprDraft(saved);
      setApprEligible(eligible);
      setApprAccessMetadata({ access_defaults: r.access_defaults, root_user_ids: r.root_user_ids });
      setApprUnavailable(false);
    } catch {
      setApprUnavailable(true);
    } finally {
      setApprLoading(false);
    }
  }, []);

  // Permission changes must refresh the picker without discarding policy edits.
  const reloadApproverDirectory = useCallback(async () => {
    try {
      const r = await approvalsApi.getPolicies();
      const eligible: Record<string, ApproverOption[]> = {};
      for (const step of APPROVAL_POLICY_STEPS) {
        const row = r.policies.find((p) => p.step === step);
        eligible[step] = r.eligible?.[step] || [...(row?.users || []), ...(row?.general_reviewer ? [row.general_reviewer] : [])].filter((u, index, all) => all.findIndex((person) => person.id === u.id) === index);
      }
      setApprEligible(eligible);
      setApprAccessMetadata({ access_defaults: r.access_defaults, root_user_ids: r.root_user_ids });
    } catch { /* keep the last directory; the policy endpoint validates every choice */ }
  }, []);

  // Root-only, so only mount work when the current user is root.
  useEffect(() => {
    if (!user?.isRoot) return;
    let alive = true;
    (async () => {
      setLoading(true);
      // Developer bundle = live catalog minus architect:use and admin:*
      // (feedback:* intentionally stays in). If the catalog is
      // unavailable the developer role is disabled below.
      try {
        const r = await authApi.permissions();
        if (alive && r.ok) {
          setDevBundle(
            (r.data.permissions || [])
              .map((p) => p.id)
              .filter((id) => id !== ARCHITECT_PERM && !id.startsWith('admin:')),
          );
        }
      } catch {
        /* leave devBundle null — developer role stays disabled */
      }
      await Promise.all([reloadUsers(), reloadTma(), reloadApprovals()]);
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [user?.isRoot, reloadUsers, reloadTma, reloadApprovals]);

  const permsForRole = useCallback((r: Role): string[] | null => {
    if (r === 'programmer') return [...PROGRAMMER_PERMS];
    if (r === 'architect') return [ARCHITECT_PERM];
    if (r === 'pricing') return [ARCHITECT_PERM, PRICING_PERM];
    if (r === 'manager') return [...FEEDBACK_PERMS];
    // A second root holds everything implicitly — no bundle is sent
    // (the backend ignores permissions for roots), so [] is enough to
    // clear the "catalog unavailable" gate below.
    if (r === 'root') return [];
    return devBundle;  // null while catalog loads / on failure
  }, [devBundle]);

  const usernameValid = username.trim().length >= 3
    && username.trim().length <= 32
    && USERNAME_RE.test(username.trim());
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const passwordValid = password.length >= 8;
  const rolePerms = permsForRole(role);
  const canSubmit = usernameValid && emailValid && passwordValid
    && rolePerms !== null && !creating;

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || rolePerms === null) return;
    // Minting a second root is irreversible full access — gate it.
    if (role === 'root'
      && !window.confirm('Создать ещё одного root? У него будет полный доступ ко всему.')) {
      return;
    }
    setCreating(true);
    setCreateError(null);
    setCreatedHint(null);
    try {
      const created = await createUser({
        username: username.trim(),
        email: email.trim(),
        password,
        displayName: displayName.trim() || undefined,
        permissions: rolePerms,
        isRoot: role === 'root',
      });
      setCreatedHint(
        `Создан ${ROLE_NAME[role]}: ${created.user.username} · пароль: ${password} — ` +
        'покажите пользователю сейчас, он больше не отобразится.',
      );
      // Reset the form (keep the role selection for rapid batches).
      setUsername('');
      setEmail('');
      setPassword('');
      setDisplayName('');
      await reloadUsers();
      await reloadApproverDirectory();
    } catch (err) {
      setCreateError(humanizeError(err));
    } finally {
      setCreating(false);
    }
  };

  // Grant/revoke a permission bundle on a row, adopting the returned
  // effective list so badges + toggle labels update immediately.
  // DESIGN41 — block / unblock. The server guards self and the last root;
  // the confirm is the only client-side ceremony.
  const setDisabled = useCallback(async (u: AdminUser, disabled: boolean) => {
    if (
      disabled &&
      !window.confirm(
        `Отключить аккаунт ${u.displayName || u.username}? Все его сессии (админка и мини-апп) завершатся сразу; инвайты перестанут работать. Включить обратно можно в любой момент.`,
      )
    ) {
      return;
    }
    setBusyId(u.id);
    setRowError(null);
    try {
      const r = await setUserDisabled(u.id, disabled);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, isDisabled: r.user.isDisabled } : x)));
    } catch (err) {
      setRowError(humanizeError(err));
    } finally {
      setBusyId(null);
    }
  }, []);

  const toggle = useCallback(async (u: AdminUser, bundle: string[], grant: boolean) => {
    setBusyId(u.id);
    setRowError(null);
    try {
      const r = await setUserPermissions(u.id, grant ? { grant: bundle } : { revoke: bundle });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, permissions: r.permissions } : x)));
      await reloadApproverDirectory();
    } catch (err) {
      setRowError(humanizeError(err));
    } finally {
      setBusyId(null);
    }
  }, [reloadApproverDirectory]);

  // ── Mini-app invite / unlink actions ──
  const createInvite = useCallback(async (u: AdminUser) => {
    setTmaBusyId(u.id);
    setInviteFor(u);
    setInviteData(null);
    setInviteError(null);
    setCopied(false);
    try {
      const data = await tmaSend<TmaInviteResponse>('/invites', 'POST', {
        user_id: u.id,
      });
      setInviteData(data);
      // Reflect the fresh pending invite on the card at once.
      setTmaStates((prev) => ({
        ...prev,
        [u.id]: prev[u.id]?.kind === 'linked'
          ? prev[u.id]
          : { kind: 'invited', expiresAt: data.expires_at ?? null },
      }));
    } catch (err) {
      setInviteError(humanizeTmaError(err));
    } finally {
      setTmaBusyId(null);
    }
  }, []);

  const unlinkUser = useCallback(async (u: AdminUser) => {
    if (
      !window.confirm(
        'Unlink this user’s Telegram account? They will need a new invite link to reconnect.',
      )
    ) {
      return;
    }
    setTmaBusyId(u.id);
    try {
      await tmaSend<{ ok: boolean }>('/links/' + encodeURIComponent(u.id), 'DELETE');
      setTmaStates((prev) => {
        const next = { ...prev };
        delete next[u.id];
        return next;
      });
    } catch (err) {
      setRowError(humanizeTmaError(err));
    } finally {
      setTmaBusyId(null);
    }
  }, []);

  /* ── Approvals: draft edits + save ─────────────────────── */

  /** Toggle one person on a step. Removing people can push the required
      count above the list length (which the server refuses), so clamp it
      down in the same update — never leave a draft that cannot be saved. */
  const toggleApprover = useCallback((step: ApprovalPolicyStep, id: string) => {
    setApprSavedStep(null);
    setApprError(null);
    setApprDraft((prev) => {
      const cur = prev[step] || { required: 1, ids: [], ordered: false };
      if (cur.generalReview) {
        return { ...prev, [step]: { ...cur, generalReviewerId: id } };
      }
      const ids = cur.ids.includes(id)
        ? cur.ids.filter((x) => x !== id)
        : cur.ids.concat(id);
      return {
        ...prev,
        [step]: { ...cur, ids, required: Math.min(cur.required, requiredCap(ids)), ordered: cur.ordered },
      };
    });
  }, []);

  const setGeneralReviewMode = (step: ApprovalPolicyStep, generalReview: boolean) => {
    setApprSavedStep(null);
    setApprError(null);
    setApprDraft((prev) => ({
      ...prev,
      [step]: { ...prev[step], generalReview },
    }));
  };

  const moveApprover = (step: ApprovalPolicyStep, index: number, direction: number) => {
    setApprSavedStep(null); setApprError(null);
    setApprDraft(prev => {
      const current = prev[step];
      const ids = [...current.ids];
      const destination = index + direction;
      if (destination < 0 || destination >= ids.length) return prev;
      [ids[index], ids[destination]] = [ids[destination], ids[index]];
      return { ...prev, [step]: { ...current, ids } };
    });
  };

  /** DESIGN46 — «await»: the named people approve one after another. */
  const setApprOrdered = useCallback((step: ApprovalPolicyStep, ordered: boolean) => {
    setApprSavedStep(null);
    setApprError(null);
    setApprDraft((prev) => {
      const cur = prev[step] || { required: 1, ids: [], ordered: false };
      // «Await» means everyone in the line signs — switching it on raises the
      // count to the list length (it can be lowered again).
      const required = ordered ? Math.max(cur.required, requiredCap(cur.ids)) : cur.required;
      return { ...prev, [step]: { ...cur, ordered, required } };
    });
  }, []);

  const setApprRequired = useCallback((step: ApprovalPolicyStep, required: number) => {
    setApprSavedStep(null);
    setApprError(null);
    setApprDraft((prev) => {
      const cur = prev[step] || { required: 1, ids: [], ordered: false };
      return { ...prev, [step]: { ...cur, required } };
    });
  }, []);

  /** Reset ONE step back to the default: anyone with the right, one approval.
      Still just a policy write — nothing already approved is touched. */
  const clearApprovalPolicy = useCallback((step: ApprovalPolicyStep) => {
    setApprSavedStep(null);
    setApprError(null);
    setApprDraft((prev) => ({ ...prev, [step]: { ...prev[step], required: 1, ids: [], ordered: false, incompleteNamedApprovers: false, generalReview: false, generalReviewerId: prev[step]?.generalReviewerId === undefined ? undefined : null } }));
  }, []);

  const saveApprovalPolicy = useCallback(
    async (step: ApprovalPolicyStep) => {
      const draft = apprDraft[step];
      if (!draft || apprSavingStep || draft.incompleteNamedApprovers) return;
      if (draft.generalReview && !draft.generalReviewerId) return;
      setApprSavingStep(step);
      setApprSavedStep(null);
      setApprError(null);
      try {
        const r = await approvalsApi.putPolicy(step, {
          required_count: draft.required,
          user_ids: draft.ids,
          ordered: draft.ordered,
          ...policyPreferences(draft),
          ...(step === 'architecture' || draft.generalReview !== undefined ? { general_reviewer_id: draft.generalReview ? draft.generalReviewerId : null } : {}),
        });
        // Adopt what the server stored, so the card can never drift from it.
        const stored: ApprovalDraft = {
          ...approvalPolicyDraft(r.policy),
          ...policyPreferences(r.policy),
          ...(step === 'architecture' || r.policy?.general_reviewer_id !== undefined ? {
            generalReview: Boolean(r.policy?.general_reviewer_id),
            generalReviewerId: r.policy?.general_reviewer_id ?? null,
          } : {}),
        };
        setApprSaved((prev) => ({ ...prev, [step]: stored }));
        setApprDraft((prev) => ({ ...prev, [step]: stored }));
        setApprSavedStep(step);
        window.setTimeout(
          () => setApprSavedStep((cur) => (cur === step ? null : cur)),
          2500,
        );
      } catch (err) {
        setApprError(
          APPROVAL_STEP_LABEL[step] +
            ': ' +
            (err instanceof Error ? err.message : 'The policy could not be saved.'),
        );
      } finally {
        setApprSavingStep(null);
      }
    },
    [apprDraft, apprSavingStep],
  );

  const closeInvite = useCallback(() => {
    setInviteFor(null);
    setInviteData(null);
    setInviteError(null);
    setCopied(false);
  }, []);

  const copyInviteLink = useCallback(async () => {
    const link = inviteData?.link;
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (insecure context / permissions) — the link is
      // shown in full above, so a manual copy still works.
      setCopied(false);
    }
  }, [inviteData]);

  const sorted = useMemo(
    () => [...users].sort((a, b) => {
      if (a.isRoot !== b.isRoot) return a.isRoot ? -1 : 1;
      return (a.displayName || a.username).localeCompare(b.displayName || b.username, 'ru');
    }),
    [users],
  );

  if (!user?.isRoot) {
    return (
      <div className="root-panel">
        <div className="root-notice">
          Раздел доступен только root-учётке.
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="root-panel"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
    >
      {/* ── Create user ─────────────────────────────────── */}
      <section className="root-section">
        <div className="section-label">
          <span className="section-label-index">01</span>
          <span className="section-label-rule" />
          <span className="section-label-title">Новый пользователь</span>
        </div>

        <form className="root-create" onSubmit={submitCreate}>
          <div className="root-create-grid">
            <div className="form-section">
              <label className="form-label">
                Логин
                <span className="form-hint">3–32, буквы/цифры/_.-</span>
              </label>
              <input
                className="form-input"
                type="text"
                autoComplete="off"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="ivan.dev"
                maxLength={32}
              />
            </div>

            <div className="form-section">
              <label className="form-label">E-mail</label>
              <input
                className="form-input"
                type="email"
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ivan@example.com"
                maxLength={254}
              />
            </div>

            <div className="form-section">
              <label className="form-label">
                Пароль
                <span className="form-hint">мин. 8 символов</span>
              </label>
              {/* type=text on purpose — root copies the plaintext once. */}
              <input
                className="form-input"
                type="text"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="сгенерируйте и сохраните"
              />
              <div className="root-pw-hint">Пароль показывается один раз — сохраните его.</div>
            </div>

            <div className="form-section">
              <label className="form-label">Имя (опц.)</label>
              <input
                className="form-input"
                type="text"
                autoComplete="off"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Имя для дашборда"
                maxLength={80}
              />
            </div>

            <div className="form-section root-create-role">
              <label className="form-label">Роль</label>
              <select
                className="form-select"
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              {role === 'developer' && devBundle === null && (
                <div className="root-role-warn">Каталог прав недоступен — роль Developer временно нельзя выдать.</div>
              )}
              {role === 'programmer' && <div className="root-pw-hint">Появится в Developers: назначения, проекты и офис. Доступ к редактированию Architect не выдаётся.</div>}
            </div>
          </div>

          <div className="form-actions">
            {createError && <span className="form-result bad">{createError}</span>}
            <button className="btn-primary" type="submit" disabled={!canSubmit}>
              {creating ? 'Создаю…' : 'Создать пользователя'}
            </button>
          </div>

          {createdHint && (
            <div className="root-created-hint">
              {createdHint}
            </div>
          )}
        </form>
      </section>

      {/* ── Users list ──────────────────────────────────── */}
      <section className="root-section">
        <div className="section-label">
          <span className="section-label-index">02</span>
          <span className="section-label-rule" />
          <span className="section-label-title">Пользователи</span>
          <span className="section-label-caption">{users.length}</span>
        </div>

        {/* Mini App connection (DESIGN26 §D): the setup form while there is no
            bot, the status card once there is one. Either way it sits above
            the list, because it decides whether the per-card invite blocks
            below can do anything at all. */}
        {!loading && <TmaSetupCard config={tmaConfig} onChanged={reloadTma} />}

        {rowError && <div className="form-result bad root-row-error">{rowError}</div>}

        {loading ? (
          <div className="root-empty">Загрузка…</div>
        ) : loadError ? (
          <div className="root-empty root-empty-err">{loadError}</div>
        ) : sorted.length === 0 ? (
          <div className="root-empty">Пользователей пока нет.</div>
        ) : (
          <div className="root-users">
            {sorted.map((u) => {
              const badge = badgeFor(u);
              const perms = u.permissions || [];
              const hasArchitect = perms.includes(ARCHITECT_PERM);
              const hasFeedback = perms.some((p) => FEEDBACK_SET.has(p));
              const hasGenerate = perms.includes(GENERATE_PERM);
              const hasEstimate = perms.includes(ESTIMATE_PERM);
              const hasPricing = perms.includes(PRICING_PERM);
              const isProgrammer = PROGRAMMER_MEMBERSHIP.every(p => perms.includes(p));
              const busy = busyId === u.id;
              const tmaState: TmaUserState = tmaStates[u.id] || { kind: 'none' };
              const tmaBusy = tmaBusyId === u.id;
              // The card shows for EVERY account once the mini app is
              // configured — its whole point is that root grants the rights
              // right here, and a card that vanished the moment access was
              // revoked would be a card you cannot use.
              const showTma = tmaConfig.configured;
              // The server's own invite rule (root or architect:use): asking
              // for an invite without it answers 400 user_not_eligible.
              const canInvite = tmaEligible(u);
              const mini = miniState(u);
              const miniLabels = u.isRoot
                ? ['Full access']
                : MINI_GRANTS.filter((g) =>
                    g.perms.some((p) => perms.includes(p)),
                  ).map((g) => g.label);
              return (
                <div key={u.id} className={`root-user${u.isDisabled ? ' root-user-off' : ''}`}>
                  <div className="root-user-main">
                    <div className="root-user-top">
                      <span className="root-user-name">{u.displayName || u.username}</span>
                      <span className={`root-badge b-${badge.kind}`}>{badge.label}</span>
                      {u.isDisabled && <span className="root-badge b-off">Отключён</span>}
                    </div>
                    <div className="root-user-meta">{u.username} · {u.email}</div>
                    <div className="root-user-meta root-user-login">
                      Последний вход: {fmtDate(u.lastLoginAt)}
                    </div>
                  </div>

                  {!u.isRoot && (
                    <div className="root-user-actions">
                      <div className="root-toggle-row">
                        <button
                          className={`btn-secondary root-toggle${isProgrammer ? ' active' : ''}`}
                          type="button"
                          disabled={busy || u.isDisabled}
                          aria-pressed={isProgrammer}
                          onClick={() => toggle(u, isProgrammer ? PROGRAMMER_MEMBERSHIP : PROGRAMMER_PERMS, !isProgrammer)}
                        >
                          {isProgrammer ? 'Убрать из программистов' : 'Добавить в программисты'}
                        </button>
                        <button
                          className="btn-secondary root-toggle"
                          type="button"
                          disabled={busy}
                          onClick={() => toggle(u, [ARCHITECT_PERM], !hasArchitect)}
                        >
                          {hasArchitect ? 'Забрать Architect' : 'Выдать Architect'}
                        </button>
                        <button
                          className="btn-secondary root-toggle"
                          type="button"
                          disabled={busy}
                          onClick={() => toggle(u, FEEDBACK_PERMS, !hasFeedback)}
                        >
                          {hasFeedback ? 'Забрать Фидбэк' : 'Выдать Фидбэк'}
                        </button>
                        <button
                          className={`btn-secondary root-toggle${u.isDisabled ? '' : ' root-toggle-danger'}`}
                          type="button"
                          disabled={busy}
                          title={u.isDisabled ? 'Вернуть доступ' : 'Заблокировать: сессии завершатся сразу'}
                          onClick={() => setDisabled(u, !u.isDisabled)}
                        >
                          {u.isDisabled ? 'Включить аккаунт' : 'Отключить аккаунт'}
                        </button>
                      </div>

                      {/* Architect sub-roles — only for a holder of
                          architect:use (DESIGN10 §P4). */}
                      {hasArchitect && (
                        <div className="root-subroles" role="group" aria-label="Architect sub-roles">
                          <span className="root-subroles-label">Sub-roles</span>
                          <button
                            className={`root-pill${hasGenerate ? ' active' : ''}`}
                            type="button"
                            disabled={busy}
                            title={GENERATE_HINT}
                            aria-pressed={hasGenerate}
                            onClick={() => toggle(u, [GENERATE_PERM], !hasGenerate)}
                          >
                            Generator
                          </button>
                          <button
                            className={`root-pill${hasEstimate ? ' active' : ''}`}
                            type="button"
                            disabled={busy}
                            title={ESTIMATE_HINT}
                            aria-pressed={hasEstimate}
                            onClick={() => toggle(u, [ESTIMATE_PERM], !hasEstimate)}
                          >
                            Estimator
                          </button>
                          <button
                            className={`root-pill${hasPricing ? ' active' : ''}`}
                            type="button"
                            disabled={busy}
                            title="Reviews prices and approves Pricing when assigned."
                            aria-pressed={hasPricing}
                            onClick={() => toggle(u, [PRICING_PERM], !hasPricing)}
                          >
                            Pricing
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Mini app access (DESIGN14 §F2.3 + DESIGN24 §T3) ──
                      Link status, the rights the invite hands over, and the
                      invite/unlink actions — one card, one story. */}
                  {showTma && (
                    <div className="root-tma root-mini">
                      <div className="root-tma-head">
                        <span className="root-tma-mark" aria-hidden="true">
                          ✈
                        </span>
                        <span className="root-tma-label">Mini app access</span>
                        {tmaState.kind === 'linked' ? (
                          <span className="root-tma-status linked">
                            {tmaState.telegramUsername
                              ? 'Linked: @' + tmaState.telegramUsername
                              : 'Linked'}
                          </span>
                        ) : tmaState.kind === 'invited' ? (
                          <span className="root-tma-status invited">
                            Invited
                            {tmaState.expiresAt
                              ? ' · until ' + fmtDateEn(tmaState.expiresAt)
                              : ''}
                          </span>
                        ) : (
                          <span className="root-tma-status none">Not linked</span>
                        )}
                        {tmaState.kind === 'linked' && tmaState.linkedAt && (
                          <span className="root-mini-since">
                            since {fmtDateEn(tmaState.linkedAt)}
                          </span>
                        )}
                      </div>

                      <p className="root-mini-lead">
                        Invite → the user opens the link in Telegram → their
                        account is linked with exactly these rights.
                      </p>

                      {u.isRoot ? (
                        <div className="root-mini-rootline">
                          Root — every right in the mini app, implicitly.
                        </div>
                      ) : (
                        <div
                          className="root-mini-rights"
                          role="group"
                          aria-label="Mini app rights"
                        >
                          <span className="root-subroles-label">Rights</span>
                          {MINI_GRANTS.map((g) => {
                            const on = g.perms.some((p) => perms.includes(p));
                            // A sub-role without the door is held but inert —
                            // say so instead of showing a lie.
                            const inert =
                              on &&
                              !hasArchitect &&
                              (g.key === 'generate' || g.key === 'estimate' || g.key === 'pricing');
                            // Granting a sub-role opens the door in the same
                            // request; revoking never touches anything else.
                            const bundle =
                              !on && !hasArchitect && (g.key === 'generate' || g.key === 'estimate' || g.key === 'pricing')
                                ? [ARCHITECT_PERM, ...g.perms]
                                : g.perms;
                            return (
                              <button
                                key={g.key}
                                className={`root-pill${on ? ' active' : ''}${inert ? ' inert' : ''}`}
                                type="button"
                                disabled={busy}
                                aria-pressed={on}
                                title={
                                  inert
                                    ? g.hint + ' Inactive until Architect access is granted.'
                                    : g.hint
                                }
                                onClick={() => toggle(u, bundle, !on)}
                              >
                                {g.label}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* What the link actually hands over. Without the door
                          the answer is «nothing» whatever else is ticked, so
                          say that rather than list rights the mini app will
                          never honour. */}
                      <div className="root-mini-summary">
                        <span className="root-mini-summary-key">
                          The invite will grant
                        </span>
                        <span
                          className={`root-mini-summary-val${
                            mini === 'blocked' || !miniLabels.length ? ' empty' : ''
                          }`}
                        >
                          {mini === 'blocked'
                            ? 'no access yet'
                            : miniLabels.length
                              ? miniLabels.join(' · ')
                              : 'nothing yet'}
                        </span>
                      </div>

                      {mini === 'blocked' && (
                        <div className="root-mini-warn">
                          This user will see an empty mini app — grant a role
                          first. Without Architect access the sign-in is
                          refused and no invite can be issued
                          {tmaState.kind === 'linked'
                            ? '; the existing link stays, but the next sign-in is turned away'
                            : ''}
                          .
                        </div>
                      )}
                      {mini === 'empty' && (
                        <div className="root-mini-warn">
                          Architect access only — this user will see an empty
                          mini app: projects open, but nothing can be approved
                          or estimated. Add Generator (architecture &amp;
                          proposal) or Estimator (estimates).
                        </div>
                      )}

                      <div className="root-tma-actions">
                        <button
                          className="btn-secondary root-toggle"
                          type="button"
                          disabled={tmaBusy || !canInvite}
                          title={
                            canInvite
                              ? 'Issue a single-use link that opens the mini app and binds this account.'
                              : 'Grant Architect access first — the server refuses an invite without it.'
                          }
                          onClick={() => createInvite(u)}
                        >
                          {tmaBusy
                            ? 'Preparing…'
                            : tmaState.kind === 'none'
                              ? 'Invite link'
                              : 'New link'}
                        </button>
                        {tmaState.kind === 'linked' && (
                          <button
                            className="btn-secondary root-toggle root-tma-unlink"
                            type="button"
                            disabled={tmaBusy}
                            onClick={() => unlinkUser(u)}
                          >
                            Unlink
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Approvals (DESIGN23 §C4) ────────────────────── */}
      {!apprUnavailable && (
        <section className="root-section root-approvals">
          <div className="root-appr-heading">
            <div>
              <h2>Stage access & approvals</h2>
              <p>Root can always view, edit and approve every stage.</p>
            </div>
            <span className="root-appr-stage-count">4 stages</span>
          </div>

          {apprError && <div className="form-result bad root-row-error">{apprError}</div>}

          {apprLoading ? (
            <div className="root-empty">Loading…</div>
          ) : (
            <div className="root-appr-grid">
              {APPROVAL_POLICY_STEPS.map((step, stepIndex) => {
                const draft = apprDraft[step] || { required: 1, ids: [], ordered: false };
                const saved = apprSaved[step] || { required: 1, ids: [], ordered: false };
                const isPricing = step === 'pricing';
                const generalReview = Boolean(draft.generalReview);
                const singlePerson = generalReview;
                const inOrder = !singlePerson && draft.ordered && draft.ids.length > 1;
                const eligible = apprEligible[step] || [];
                const unavailableIds = draft.ids.filter(id => !eligible.some(person => person.id === id));
                const generalReviewer = eligible.find((u) => u.id === draft.generalReviewerId);
                const cap = requiredCap(draft.ids);
                const dirty = !sameDraft(draft, saved);
                const draftIsDefault =
                  !generalReview && !draft.incompleteNamedApprovers && draft.ids.length === 0 && draft.required === 1;
                const busy = apprSavingStep === step;
                const selectedNames = draft.ids.map((id) => {
                  const person = eligible.find((u) => u.id === id);
                  return person ? approverLabel(person) : 'Unavailable account';
                });
                return (
                  <div className={`root-appr-card${dirty ? ' is-dirty' : ''}`} key={step} role="region" aria-label={APPROVAL_STEP_LABEL[step] + ' policy'}>
                    <div className="root-appr-card-head">
                      <span className="root-appr-index" aria-hidden="true">{stepIndex + 1}</span>
                      <div className="root-appr-card-title">
                        <h3 className="root-appr-step">{APPROVAL_STEP_LABEL[step]}</h3>
                        <span className="root-appr-hint">{APPROVAL_STEP_HINT[step]}</span>
                      </div>
                      {draftIsDefault ? (
                        <span className="root-appr-state default">{draft.access != null ? 'Approval: Root' : isPricing ? 'Choose an approver' : 'Default'}</span>
                      ) : (
                        <span className="root-appr-state set">
                          {generalReview ? 'General review' : `${draft.required} approval${draft.required === 1 ? '' : 's'}`}
                        </span>
                      )}
                    </div>

                    <StagePolicyPreferences value={draft} people={eligible} defaults={apprAccessMetadata.access_defaults?.[step]} rootIds={apprAccessMetadata.root_user_ids} disabled={busy} onChange={patch => {
                      setApprSavedStep(null); setApprError(null);
                      setApprDraft(prev => ({ ...prev, [step]: { ...prev[step], ...patch } }));
                    }} />

                    {(
                      <div className="root-appr-picker">
                        <span className="root-appr-picker-label">Approval flow</span>
                        <div className="root-appr-people" role="group" aria-label={APPROVAL_STEP_LABEL[step] + ' approval flow'}>
                          <button type="button" className={`root-pill${!generalReview ? ' active' : ''}`} aria-pressed={!generalReview} disabled={busy} onClick={() => setGeneralReviewMode(step, false)}>Named approvers</button>
                          <button type="button" className={`root-pill${generalReview ? ' active' : ''}`} aria-pressed={generalReview} disabled={busy} onClick={() => setGeneralReviewMode(step, true)}>Author → General reviewer</button>
                        </div>
                      </div>
                    )}

                    <div className="root-appr-picker">
                      <span className="root-appr-picker-label">{generalReview ? 'General reviewer' : 'Approvers'}</span>
                      {draft.incompleteNamedApprovers ? <p className="stage-policy-note" role="alert">Reviewer list unavailable. Reload before saving this stage.</p>
                        : generalReview && draft.generalReviewerId && !generalReviewer ? <p className="stage-policy-note" role="status">The General reviewer is unavailable. Choose a replacement to resume reviews.</p>
                        : !generalReview && unavailableIds.length > 0 ? <p className="stage-policy-note" role="status">Unavailable reviewers are kept until you remove them.</p> : null}
                      <div
                        className="root-appr-people"
                        role={singlePerson ? 'radiogroup' : 'group'}
                        aria-label={generalReview ? 'General reviewer' : APPROVAL_STEP_LABEL[step] + ' approvers'}
                      >
                      {eligible.length ? (
                        eligible.map((u) => {
                          const on = generalReview ? draft.generalReviewerId === u.id : draft.ids.includes(u.id);
                          const pos = inOrder && on ? draft.ids.indexOf(u.id) + 1 : 0;
                          return (
                            <button
                              key={u.id}
                              className={`root-pill${on ? ' active' : ''}${pos ? ' numbered' : ''}`}
                              type="button"
                              disabled={busy}
                              role={singlePerson ? 'radio' : undefined}
                              aria-checked={singlePerson ? on : undefined}
                              aria-pressed={singlePerson ? undefined : on}
                              title={pos ? `${approverLabel(u)} — approves ${ordinal(pos)}` : u.username}
                              onKeyDown={singlePerson ? (e) => {
                                if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(e.key)) return;
                                e.preventDefault();
                                const offset = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
                                const next = eligible[(eligible.indexOf(u) + offset + eligible.length) % eligible.length];
                                toggleApprover(step, next.id);
                                const radios = e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
                                radios?.[eligible.indexOf(next)]?.focus();
                              } : undefined}
                              onClick={() => toggleApprover(step, u.id)}
                            >
                              {pos ? <span className="root-pill-num" aria-hidden="true">{pos}</span> : null}
                              {approverLabel(u)}
                              {on && !pos ? <span className="root-appr-person-check" aria-hidden="true">✓</span> : null}
                            </button>
                          );
                        })
                      ) : (
                        <span className="root-appr-empty">
                          No eligible accounts yet — {APPROVAL_STEP_ROLE[step]}
                        </span>
                      )}
                      {!generalReview && unavailableIds.map(id => <button key={id} type="button" className="root-pill active"
                        disabled={busy} title={id} aria-label={`Remove unavailable reviewer ${id}`} onClick={() => toggleApprover(step, id)}>Unavailable reviewer ×</button>)}
                      </div>
                    </div>

                    {!singlePerson && (
                      <div className="root-appr-controls">
                        <label className="root-appr-count">
                          <span>Approvals needed</span>
                          <select
                            className="form-select"
                            value={draft.required}
                            disabled={busy}
                            onChange={(e) => setApprRequired(step, Number(e.target.value))}
                          >
                            {Array.from({ length: cap }, (_, i) => i + 1).map((n) => (
                              <option key={n} value={n}>{n}</option>
                            ))}
                          </select>
                        </label>
                        {draft.ids.length > 1 && (
                          <label className="root-appr-order" title="Requests follow the numbered order. Use the arrows to reorder reviewers.">
                            <input
                              type="checkbox"
                              checked={draft.ordered}
                              disabled={busy}
                              onChange={(e) => setApprOrdered(step, e.target.checked)}
                            />
                            <span>Approve in order</span>
                          </label>
                        )}
                      </div>
                    )}

                    {inOrder && <div className="root-appr-sequence" aria-label="Approval order">
                      {draft.ids.map((id, index) => <div key={id}><span>{index + 1}. {selectedNames[index]}</span>
                        <button type="button" className="btn-secondary root-toggle" aria-label={`Move ${selectedNames[index]} earlier`} disabled={busy || index === 0} onClick={() => moveApprover(step, index, -1)}>↑</button>
                        <button type="button" className="btn-secondary root-toggle" aria-label={`Move ${selectedNames[index]} later`} disabled={busy || index === draft.ids.length - 1} onClick={() => moveApprover(step, index, 1)}>↓</button>
                      </div>)}
                    </div>}

                    <div className="root-appr-summary">
                      {draft.incompleteNamedApprovers ? 'Reviewers unavailable'
                        : generalReview
                        ? generalReviewer
                          ? `Author confirms and submits → ${approverLabel(generalReviewer)} gives final approval. Their own ${step === 'architecture' ? 'architectures' : 'documents'} need only their approval.`
                          : draft.generalReviewerId ? 'Author confirms and submits → unavailable General reviewer.' : 'Choose a General reviewer. Each author confirms and submits before the final review.'
                        : inOrder
                          ? `${selectedNames.join(' → ')} · ${draft.required} of ${draft.ids.length}`
                          : draft.ids.length
                            ? `${draft.required} of ${draft.ids.length} approvals · requests sent together`
                            : draft.access != null ? 'No approvers selected. Root can approve.' : isPricing ? 'Choose who receives pricing requests.' : 'Any eligible person can approve.'}
                    </div>

                    <StagePolicyNotifications value={draft} people={eligible} step={step} defaults={apprAccessMetadata.access_defaults?.[step]} rootIds={apprAccessMetadata.root_user_ids} disabled={busy} onChange={patch => {
                      setApprSavedStep(null); setApprError(null);
                      setApprDraft(prev => ({ ...prev, [step]: { ...prev[step], ...patch } }));
                    }} />

                    <div className="root-appr-foot">
                      <span className={'root-appr-save-state' + (dirty ? ' is-dirty' : '')} aria-live="polite">
                        {busy ? 'Saving…' : dirty ? 'Unsaved changes' : apprSavedStep === step ? 'Saved ✓' : isPricing && !draft.ids.length ? 'Not set' : 'Saved'}
                      </span>
                      <div className="root-appr-actions">
                        {dirty && (
                          <button className="btn-secondary root-toggle" type="button" disabled={busy} onClick={() => {
                            setApprDraft((prev) => ({ ...prev, [step]: saved }));
                            setApprError(null);
                          }}>Discard</button>
                        )}
                        {!singlePerson && !draftIsDefault && (
                          <button
                            className="btn-secondary root-toggle"
                            type="button"
                            disabled={busy}
                            onClick={() => clearApprovalPolicy(step)}
                          >
                            Reset approval
                          </button>
                        )}
                        <button
                          className="btn-primary root-appr-save"
                          type="button"
                          disabled={busy || !dirty || draft.incompleteNamedApprovers || (generalReview && !draft.generalReviewerId)}
                          onClick={() => saveApprovalPolicy(step)}
                        >
                          {busy ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* ── Invite-link modal (DESIGN14 §F2.3) ── */}
      {inviteFor && (
        <div
          className="root-tma-modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeInvite();
          }}
        >
          <div
            className="root-tma-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Mini App invite link"
          >
            <div className="root-tma-modal-head">
              <span className="root-tma-modal-title">Invite link</span>
              <button
                className="root-tma-modal-close"
                type="button"
                aria-label="Close"
                onClick={closeInvite}
              >
                ✕
              </button>
            </div>
            <div className="root-tma-modal-body">
              <div className="root-tma-modal-for">
                For: {inviteFor.displayName || inviteFor.username}
              </div>

              {inviteError ? (
                <div className="root-tma-modal-error">{inviteError}</div>
              ) : !inviteData ? (
                <div className="root-tma-modal-loading">Creating link…</div>
              ) : (
                <>
                  <div className="root-tma-link" title={inviteData.link}>
                    {inviteData.link}
                  </div>
                  <button
                    className="btn-primary root-tma-copy"
                    type="button"
                    onClick={copyInviteLink}
                  >
                    {copied ? 'Copied ✓' : 'Copy'}
                  </button>
                  {/* The server keeps only the hash of this token, so closing
                      the modal really is the last time anyone sees the link. */}
                  <div className="root-tma-modal-once">
                    This link is shown once — copy it now.
                  </div>
                  <div className="root-tma-modal-hint">
                    Send this link to the user — it opens the mini app in Telegram
                    and links their account.
                  </div>
                  {inviteData.expires_at && (
                    <div className="root-tma-modal-expires">
                      Valid until {fmtDateEn(inviteData.expires_at)}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default RootPanel;
